import { useTranslation } from 'react-i18next';
import { cn, PaletteUtils } from '@shared/utils';
import { Button, Pagination, Spinner, Text } from '../index';

export enum TablePaginationCountOfItems {
  Five = 5,
  Ten = 10,
  Twenty = 20,
  Thirty = 30,
}

export enum TableCellType {
  Text,
  Custom,
}

interface TableProps {
  className?: string;
  tableClassName?: string;
  textClassName?: string;
  isScrollable?: boolean;
  isLoading?: boolean;
  error?: string | null;
  columns: string[];
  items: { type: TableCellType; value: any; width?: string; className?: string }[][];
  onClick?: (rowIndex: number) => void;
  onRefresh?: () => void;
  pagination?: {
    currentPage: number;
    totalItemCount: number;
    countItemPage: TablePaginationCountOfItems;
    onChangePage: (pageNumber: number) => void;
    onChangeCountItemPage: (pageNumber: number) => void;
  };
}

export const Table = (props: TableProps) => {
  const { t } = useTranslation();

  return (
    <>
      <div className={cn('flex w-full flex-col', PaletteUtils.isVcfcPalette && 'rounded-lg border border-light/40', props.className)}>
        <div
          className={cn(
            'flex w-full bg-white py-1 dark:bg-soft-black',
            PaletteUtils.isVcfcPalette && 'rounded-xl',
            props.isScrollable && 'overflow-x-auto',
          )}
        >
          <table className={cn('w-full text-sm', props.tableClassName)}>
            <thead
              className={cn(
                'font-semibold uppercase text-slate opacity-40 dark:text-white dark:opacity-50',
                PaletteUtils.isVcfcPalette && 'text-[10px]',
                PaletteUtils.isInsigniaPalette && 'text-xs',
              )}
            >
              <tr>
                {props.columns.map((column, idx) => (
                  <th
                    key={idx}
                    scope='col'
                    className={cn('px-2 py-4 text-left', idx === 0 && 'pl-7 pr-0', idx === props.columns.length - 1 && 'pr-7')}
                    style={{ width: props.items?.[0]?.[idx]?.width }}
                  >
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {props.isLoading && (
                <tr>
                  <td colSpan={props.columns.length} className='overflow-hidden py-4'>
                    <Spinner />
                  </td>
                </tr>
              )}
              {!props.isLoading && !props.error && props.items.length > 0 && (
                <>
                  {props.items.map((rowItem, rowIdx) => (
                    <tr
                      key={rowIdx}
                      className={cn(
                        props.onClick && ['cursor-pointer transition-transform duration-200', 'hover:shadow-lg dark:hover:shadow-lg-dark'],

                        rowIdx !== props.items.length - 1 && 'border-b border-b-gray-black',
                      )}
                      onClick={() => props.onClick?.(rowIdx)}
                    >
                      {rowItem.map((cell, cellIdx) => {
                        const cellContent =
                          cell.type === TableCellType.Text ? (
                            <Text className={cn('whitespace-nowrap', props.textClassName, cell.className)} size='sm' showTitleOnTruncate>
                              {cell.value}
                            </Text>
                          ) : (
                            cell.value
                          );

                        return (
                          <td
                            key={cellIdx}
                            className={cn(
                              'px-1 py-4 text-left',
                              cellIdx === 0 && '!pl-7 !pr-0',
                              cellIdx === rowItem.length - 1 && '!pr-7',
                              cellIdx !== 0 && 'px-2',
                            )}
                          >
                            {cellContent}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </>
              )}
              {!props.isLoading && props.error && (
                <tr>
                  <td colSpan={props.columns.length} className='py-4 text-center'>
                    {props.error}
                    {props.onRefresh && (
                      <Button className='mx-auto mt-4' color='primary' onClick={props.onRefresh}>
                        Refresh
                      </Button>
                    )}
                  </td>
                </tr>
              )}
              {!props.isLoading && !props.error && props.items.length === 0 && (
                <tr>
                  <td colSpan={props.columns.length} className='py-4 text-center'>
                    {t('noData.default')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {PaletteUtils.isInsigniaPalette && props.pagination && <Pagination className='mt-6' {...props.pagination} />}
      </div>
      {PaletteUtils.isVcfcPalette && props.pagination && <Pagination className='mt-6' {...props.pagination} />}
    </>
  );
};
